<template>
  <section class="activity-center-feature-section">
    <button
      class="activity-center-feature-section__header"
      :aria-expanded="isOpen"
      @click="isOpen = !isOpen"
    >
      <span
        class="activity-center-feature-section__chevron"
        :class="{ 'activity-center-feature-section__chevron--open': isOpen }"
        aria-hidden="true"
      ></span>

      <span>{{ section.label }}</span>
    </button>

    <div
      v-if="isOpen"
      class="activity-center-feature-section__content"
    >
      <div
        v-if="!visibleStatusGroups.length"
        class="activity-center-feature-section__empty-state"
      >
        There are currently no jobs running
      </div>

      <div
        v-for="group in visibleStatusGroups"
        :key="group.name"
        class="activity-center-feature-section__group"
      >
        <div
          class="activity-center-feature-section__group-header"
          :style="{ color: group.color, backgroundColor: group.backgroundColor }"
        >
          <span>{{ group.label.toUpperCase() }} ({{ group.activities.length }})</span>
        </div>

        <div
          v-for="timePeriod in group.timePeriodGroups"
          :key="timePeriod.name"
          class="activity-center-feature-section__time-period"
        >
          <div class="activity-center-feature-section__time-period-header">
            <span>{{ timePeriod.label }}</span>
            <span
              class="activity-center-feature-section__time-period-chevron"
              aria-hidden="true"
            ></span>
          </div>

          <div class="activity-center-feature-section__cards">
            <activity-center-activity-card
              v-for="activity in timePeriod.activities"
              :key="activity.id"
              :activity="activity"
              @navigate="$emit('navigate')"
              @retry="$emit('retry')"
              @retry:error="$emit('retry:error')"
            ></activity-center-activity-card>
          </div>
        </div>
      </div>

      <button
        v-if="remainingActivitiesCount > 0"
        class="activity-center-feature-section__show-more"
        @click="isShowingAllActivities = true"
      >
        Show {{ remainingActivitiesCount }} more
      </button>
    </div>
  </section>
</template>

<script>
  import { computed, ref, watch } from 'vue'
  import {
    ACTIVITY_CENTER_STATUS_GROUPS,
    getActivityCenterTimePeriodGroups
  } from '@/constants/activityCenter'
  import ActivityCenterActivityCard from './ActivityCenterActivityCard.vue'

  export default {
    name: 'ActivityCenterFeatureSection',
    components: {
      ActivityCenterActivityCard
    },
    props: {
      section: {
        type: Object,
        required: true
      },
      focusedNotificationId: {
        type: String,
        default: ''
      },
      initiallyOpen: {
        type: Boolean,
        default: false
      }
    },
    emits: ['navigate', 'retry', 'retry:error'],
    setup(props) {
      const isOpen = ref(props.initiallyOpen)
      const isShowingAllActivities = ref(false)

      watch(
        () => props.initiallyOpen,
        initiallyOpen => {
          isOpen.value = initiallyOpen
        }
      )

      const visibleActivities = computed(() => {
        if (isShowingAllActivities.value) {
          return props.section.activities
        }

        const activities = props.section.activities.slice(0, 3)
        const focusedActivity = props.section.activities.find(
          ({ id }) => id === props.focusedNotificationId
        )

        if (!focusedActivity || activities.includes(focusedActivity)) {
          return activities
        }

        return [
          ...activities.slice(0, 2),
          focusedActivity
        ]
      })

      const remainingActivitiesCount = computed(() => (
        isShowingAllActivities.value
          ? 0
          : Math.max(0, props.section.activities.length - 3)
      ))

      const visibleStatusGroups = computed(() => ACTIVITY_CENTER_STATUS_GROUPS
        .map(statusGroup => {
          const activities = props.section.activities
            .filter(activity => activity.status === statusGroup.name)
          const visibleGroupActivities = visibleActivities.value
            .filter(activity => activity.status === statusGroup.name)

          return {
            ...statusGroup,
            activities,
            timePeriodGroups: getActivityCenterTimePeriodGroups(
              visibleGroupActivities
            )
          }
        })
        .filter(statusGroup => statusGroup.timePeriodGroups.length))

      return {
        isOpen,
        isShowingAllActivities,
        visibleStatusGroups,
        remainingActivitiesCount
      }
    }
  }
</script>

<style lang="scss" scoped>
  .activity-center-feature-section {
    border: 1px solid $mi-silver-100;
    border-radius: 6px;
    overflow: hidden;
    background-color: $mi-silver-50;

    &:not(:last-child) {
      border-bottom: 0;
      border-radius: 0;
    }

    &:first-child {
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
    }

    &:last-child {
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }

    &__header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 24px;
      border: 0;
      border-bottom: 1px solid $mi-silver-200;
      background-color: $mi-silver-50;
      cursor: pointer;
      font-family: $mi-typography-font-family-condensed;
      font-weight: 700;
      font-size: 20px;
      line-height: 28px;
      color: $mi-anthracite-800;
      text-align: left;
    }

    &__chevron {
      width: 10px;
      height: 10px;
      flex-shrink: 0;
      border: solid currentColor;
      border-width: 0 3px 3px 0;
      transform: rotate(-45deg);
      transition: transform .15s ease;
    }

    &__chevron--open {
      transform: rotate(45deg);
    }

    &__content {
      padding: 24px 16px;
      min-height: 96px;
    }

    &__empty-state {
      padding: 14px 0;
      font-family: $mi-typography-font-family-condensed;
      font-size: 14px;
      line-height: 21px;
      color: $mi-anthracite-500;
      text-align: center;
    }

    &__group:not(:last-child) {
      margin-bottom: 24px;
    }

    &__group-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 4px 14px;
      margin-bottom: 6px;
      font-family: $mi-typography-font-family;
      font-weight: 700;
      font-size: 14px;
      line-height: 150%;
    }

    &__cards {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &__show-more {
      min-height: auto;
      padding: 0;
      margin-top: 10px;
      border: 0;
      background: transparent;
      cursor: pointer;
      font-family: $mi-typography-font-family-condensed;
      font-size: 14px;
      line-height: 21px;
      color: $mi-anthracite-800;
      text-decoration: underline;
      text-transform: none;
    }

    &__time-period:not(:last-child) {
      margin-bottom: 18px;
    }

    &__time-period-header {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      padding: 0 20px 8px;
      font-family: $mi-typography-font-family-condensed;
      font-size: 13px;
      line-height: 20px;
      color: $mi-anthracite-400;
    }

    &__time-period-chevron {
      width: 0;
      height: 0;
      border-left: 7px solid transparent;
      border-right: 7px solid transparent;
      border-top: 7px solid currentColor;
    }
  }
</style>
